<script lang="ts" setup>
import { ElNotification, type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'

import { useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import ActivityAvatar from '@/components/CampaignParticipants/CampaignParticipantDetails/components/ActivityAvatar.vue'
import type { AvatarDataType } from '@/types/campaignParticipant.types'
import { RichTextEditor } from '@/ui-library'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'

const props = defineProps<{
  avatarData: AvatarDataType
  participantId: number | string
  onCommentAdd: () => void
  campaignId: number
}>()

const { addComment } = useUpdateCampaignParticipant(props.campaignId)

const isDirty = ref(false)
const formRef = ref<FormInstance>()
const formData = reactive({ comment: '' })
const rules = reactive<FormRules<{ comment: string }>>({
  comment: [
    { max: 15000, message: 'Field must be shorter than 15000 symbols' },
    { required: true, message: 'This field is required' },
  ],
})

const handleSubmit = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate(async (valid) => {
    if (valid) {
      isDirty.value = false
      await addComment(props.participantId, formData.comment)
      props.onCommentAdd()
      formEl.resetFields(['comment'])
      ElNotification.success('Comment was successfully added.')
    }
  })
}
const handleReset = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }
  formEl.resetFields(['comment'])
  isDirty.value = false
}
</script>
<template>
  <BoxComponent>
    <FlexComponent :gap="8" align="flex-start">
      <ActivityAvatar :color="avatarData.color" :abbreviation="avatarData.abbreviation" />
      <el-form ref="formRef" class="w-100" :model="formData" :rules="rules" @submit.prevent="handleSubmit(formRef)">
        <el-form-item prop="comment">
          <RichTextEditor v-model="formData.comment" placeholder="Write a comment" />
        </el-form-item>
        <FlexComponent v-if="formData.comment?.length > 0" :gap="8">
          <ButtonComponent variant="accent" type="submit">Save</ButtonComponent>
          <el-button round @click="handleReset(formRef)">Cancel</el-button>
        </FlexComponent>
      </el-form>
    </FlexComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped></style>
